import { RightArrowIcon } from "assets/icons";
import { toastServerError } from "helpers";
import { useGetAllCategories } from "modules/categories";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input, Select } from "ui";

import { useBlockAffinityPairing } from "../../apis";
import { buildCategoryOptions, flattenCategoryTree } from "../../helpers";

import styles from "./AffinityBlacklistForm.module.scss";

const AffinityBlacklistForm = () => {
  const { t } = useTranslation();

  const { categories } = useGetAllCategories();
  // Every level, unlike a rule: blocking a parent covers each leaf under it.
  const categoryOptions = useMemo(() => {
    const flat = flattenCategoryTree(categories);

    return buildCategoryOptions(flat, flat);
  }, [categories]);

  const [fromCategoryId, setFromCategoryId] = useState<string | null>(null);
  const [toCategoryId, setToCategoryId] = useState<string | null>(null);
  const [bothDirections, setBothDirections] = useState(true);
  const [reason, setReason] = useState("");

  const { mutate: block, isLoading } = useBlockAffinityPairing({
    onSuccess: (result) => {
      toast.success(
        result.removedRules > 0
          ? `${t("Pairing blocked")}, ${result.removedRules} ${t("rules removed")}`
          : t("Pairing blocked")
      );
      setFromCategoryId(null);
      setToCategoryId(null);
      setBothDirections(true);
      setReason("");
    },
    onError: toastServerError
  });

  const handleBlock = () => {
    if (!fromCategoryId || !toCategoryId) {
      toast.error(t("Pick both categories"));
      return;
    }

    if (fromCategoryId === toCategoryId) {
      toast.error(t("A category cannot pair with itself"));
      return;
    }

    block({
      fromCategoryId,
      toCategoryId,
      bothDirections,
      reason: reason.trim() || undefined
    });
  };

  return (
    <div className={styles.form}>
      <div className={styles.form__row}>
        <div className={styles.form__field}>
          <Select
            label={t("Category")}
            placeholder={t("Pick a category")}
            options={categoryOptions}
            value={fromCategoryId}
            withFilterInput
            onChange={(selected) => setFromCategoryId(selected as string)}
          />
        </div>

        <span className={styles.form__arrow}>
          <RightArrowIcon width={20} height={20} />
        </span>

        <div className={styles.form__field}>
          <Select
            label={t("Never pair with")}
            placeholder={t("Pick a category")}
            options={categoryOptions}
            value={toCategoryId}
            withFilterInput
            onChange={(selected) => setToCategoryId(selected as string)}
          />
        </div>
      </div>

      <div className={styles.form__row}>
        <div className={styles.form__field}>
          <Input
            label={t("Reason (optional)")}
            placeholder={t("Why this pairing makes no sense")}
            value={reason}
            maxLength={200}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>

        <Checkbox
          className={styles.form__checkbox}
          label={t("Both directions")}
          checked={bothDirections}
          onChange={(event) => setBothDirections(event.target.checked)}
        />

        <Button
          className={styles.form__submit}
          loading={isLoading}
          onClick={handleBlock}
        >
          {t("Block pairing")}
        </Button>
      </div>
    </div>
  );
};

export default AffinityBlacklistForm;
